// =============================================================================
// src/lib/referralsAuthsCore.js - pure helpers for Referrals & Auths (Oct 2026).
// Migration 20261207100000_referrals_auths. No imports, so node:test can load
// it (src/lib/__tests__/referralsAuths.test.mjs).
// =============================================================================

export const CREATOR_ROLES = ["Owner", "Manager", "Provider", "RN", "LPN", "Medical Assistant"];
export function canCreate(role) {
  return CREATOR_ROLES.indexOf(String(role || "")) !== -1;
}

export const REFERRAL_NEXT = { Draft: "Sent", Sent: "Scheduled", Scheduled: "Completed", Completed: "Report Received" };
export const REFERRAL_NEXT_LABEL = {
  Draft: "Mark sent", Sent: "Mark scheduled", Scheduled: "Mark seen", Completed: "Report received",
};
export const REFERRAL_TONE = {
  Draft: "neutral", Sent: "blue", Scheduled: "amber", Completed: "purple", "Report Received": "green", Declined: "red", Cancelled: "neutral",
};

const STALL = {
  not_sent: { text: "Not sent yet", tone: "amber" },
  not_scheduled: { text: "Not scheduled", tone: "red" },
  visit_unconfirmed: { text: "Visit not confirmed", tone: "amber" },
  no_report: { text: "No report yet", tone: "red" },
};
export function stallLabel(code) {
  return STALL[String(code || "")] || null;
}

export const AUTH_STATUS_TONE = {
  Draft: "neutral", Pending: "blue", Approved: "green", "Partially approved": "amber", Denied: "red", Cancelled: "neutral",
};
const FLAG = {
  not_submitted: { text: "Not submitted", tone: "amber" },
  plan_overdue: { text: "Plan is late", tone: "red" },
  plan_due_soon: { text: "Plan decision due today", tone: "amber" },
  decision_overdue: { text: "Decision overdue", tone: "red" },
  appeal_open: { text: "Appeal open", tone: "amber" },
  appeal_due: { text: "Appeal due soon", tone: "red" },
  appeal_missed: { text: "Appeal deadline passed", tone: "red" },
  appeal_waiting: { text: "Appeal submitted", tone: "blue" },
  expiring: { text: "Ending soon", tone: "amber" },
  expired: { text: "Expired", tone: "red" },
};
export function authFlagLabel(code) {
  return FLAG[String(code || "")] || null;
}

export const SUBMIT_VIA = [
  { value: "portal", label: "Payer portal" },
  { value: "fax", label: "Fax" },
  { value: "phone", label: "Phone" },
  { value: "mail", label: "Mail" },
  { value: "other", label: "Other" },
];
export const DECISIONS = ["Approved", "Partially approved", "Denied", "Cancelled"];
export const APPEAL_STEPS = [
  { value: "planned", label: "Appeal planned" },
  { value: "submitted", label: "Appeal submitted" },
  { value: "won", label: "Appeal won (approved)" },
  { value: "lost", label: "Appeal lost" },
  { value: "none", label: "Not appealing" },
];

export function daysSince(iso, today) {
  if (!iso) return null;
  const a = new Date(String(iso).slice(0, 10) + "T12:00:00");
  const b = today ? new Date(String(today).slice(0, 10) + "T12:00:00") : new Date();
  return Math.max(0, Math.round((b - a) / 86400000));
}

export function shortDate(v) {
  if (!v) return "";
  const s = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T12:00:00") : new Date(s);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined });
}

// One line on where a referral stands, e.g. "Sent 9 days ago".
export function stepLine(r, today) {
  const d = daysSince(r && r.step_since, today);
  const when = d == null ? "" : d === 0 ? " today" : d === 1 ? " 1 day ago" : " " + d + " days ago";
  const verb = { Draft: "Created", Sent: "Sent", Scheduled: "Scheduled", Completed: "Seen", "Report Received": "Report in" }[r && r.status] || (r && r.status) || "";
  return verb + when;
}

export function filterRows(rows, q) {
  const list = Array.isArray(rows) ? rows : [];
  const s = String(q || "").trim().toLowerCase();
  if (!s) return list;
  return list.filter((r) => [r.first_name, r.last_name, r.mrn, r.to_specialty, r.to_provider_name, r.service, r.payer, r.service_code]
    .some((x) => String(x || "").toLowerCase().indexOf(s) !== -1));
}

export function startErrorText(code) {
  const map = {
    no_mobile: "The patient has no mobile number on file.",
    already_open: "The patient has another text conversation open. Try again after it closes.",
    asked_recently: "The patient was asked in the last 3 days.",
    not_askable: "Only sent or scheduled referrals can be checked by text.",
    not_found: "This referral was not found. Reload.",
    send_failed: "The text could not be sent. Try again.",
    not_sent_patient_sms_opted_out: "The patient opted out of texts. Call instead.",
    not_sent_no_sms_consent: "The patient has not agreed to texts. Call instead.",
    mfa_required: "Sign in again with two-step verification.",
    tier_required: "AI drafts are part of Pro and Command.",
    feature_disabled: "AI drafts are off in Settings > AI Features.",
    phi_gate_closed: "AI features are off until onboarding and the BAA are complete.",
    ai_call_failed: "The AI did not answer. Try again.",
    not_editable: "This request can no longer be edited.",
    not_allowed: "Your role cannot do this.",
  };
  const c = String(code || "");
  if (map[c]) return map[c];
  if (c.indexOf("not_sent_after_hours") === 0) return "It is after hours. Try again in business hours.";
  if (c.indexOf("not_sent") === 0) return "The text was not sent (" + c.replace("not_sent_", "").replace(/_/g, " ") + ").";
  return c ? "Could not finish (" + c.replace(/_/g, " ") + ")." : "Something went wrong.";
}

export function rpcErrorText(e) {
  const m = String((e && e.message) || e || "");
  if (/not_authorized/.test(m)) return "Your role cannot do this.";
  if (/patient_required/.test(m)) return "Choose the patient.";
  if (/service_required/.test(m)) return "Enter the service being requested.";
  if (/not_draft/.test(m)) return "This request was already submitted.";
  if (/illegal_transition/.test(m)) return "That step is not allowed from here.";
  if (/not_requestable/.test(m)) return "A records request needs a sent, scheduled or seen referral.";
  if (/invalid_service/.test(m)) return "Enter a service name of 2 to 120 characters.";
  if (/no_clock/.test(m)) return "This request has no federal decision clock.";
  if (/not_overdue/.test(m)) return "The plan still has time to decide.";
  if (/bad_urgency|bad_rule/.test(m)) return "Pick standard or expedited and a plan type.";
  if (/not_denied/.test(m)) return "Appeal letters are for denied requests.";
  if (/too_long/.test(m)) return "Keep the letter under 8,000 characters.";
  if (/not_editable/.test(m)) return "This request can no longer be changed.";
  if (/invalid_code/.test(m)) return "A CPT / HCPCS code is 4 to 7 letters or numbers (for example 72148 or E0601).";
  if (/payer_or_plan_type/.test(m)) return "A rule is for one payer or one type of plan, not both.";
  if (/invalid_payer/.test(m)) return "Enter the payer name.";
  if (/invalid_plan_type/.test(m)) return "Pick the type of plan.";
  if (/invalid_requirement/.test(m)) return "Pick whether prior auth is required.";
  if (/invalid_note/.test(m)) return "Keep the note under 500 characters.";
  if (/not_found/.test(m)) return "That was removed. Refresh and try again.";
  return m || "Something went wrong.";
}

function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]));
}

// Printable records request letter (opened in a new window for print / fax).
export function recordsLetterHtml(d, todayLabel) {
  const pr = (d && d.practice) || {};
  const p = (d && d.patient) || {};
  const r = (d && d.referral) || {};
  const rp = d && d.referring_provider;
  const to = [r.to_provider_name, r.to_facility, r.to_specialty].filter(Boolean).join(" - ");
  return "<!doctype html><html><head><meta charset=\"utf-8\"><title>Records request</title>"
    + "<style>body{font-family:Georgia,serif;max-width:720px;margin:40px auto;color:#111;line-height:1.5;font-size:14px}"
    + "h1{font-size:18px;margin:0}table{border-collapse:collapse;margin:12px 0}td{padding:4px 12px 4px 0;vertical-align:top}"
    + ".muted{color:#555}.sig{margin-top:40px}@media print{button{display:none}}</style></head><body>"
    // audit-exempt: the server writes the Export audit row (referral_records_request) before this letter is built.
    + "<button onclick=\"window.print()\" style=\"float:right\">Print</button>"
    + "<h1>" + esc(pr.name) + "</h1><div class=\"muted\">" + esc(pr.address || "") + "<br>Phone " + esc(pr.phone || "")
    + (pr.fax ? " - Fax " + esc(pr.fax) : "") + (pr.npi ? " - NPI " + esc(pr.npi) : "") + "</div>"
    + "<p>" + esc(todayLabel || "") + "</p>"
    + "<p>To: " + esc(to || "Specialist office") + (r.to_phone ? "<br>Phone " + esc(r.to_phone) : "") + (r.to_address ? "<br>" + esc(r.to_address) : "") + "</p>"
    + "<p><b>Request for consultation report" + (d && d.request_number > 1 ? " (request " + esc(d.request_number) + ")" : "") + "</b></p>"
    + "<table><tr><td>Patient</td><td>" + esc(p.name) + "</td></tr><tr><td>Date of birth</td><td>" + esc(p.dob || "") + "</td></tr>"
    + (p.mrn ? "<tr><td>Our MRN</td><td>" + esc(p.mrn) + "</td></tr>" : "")
    + "<tr><td>Referred</td><td>" + esc(shortDate(r.sent_at)) + "</td></tr>"
    + (r.completed_at ? "<tr><td>Seen</td><td>" + esc(shortDate(r.completed_at)) + "</td></tr>" : "")
    + (r.reason ? "<tr><td>Reason for referral</td><td>" + esc(r.reason) + "</td></tr>" : "") + "</table>"
    + "<p>We referred this patient to your office. Please send your consultation note and any results or recommendations"
    + (pr.fax ? " to our fax at " + esc(pr.fax) : " to our office") + " so we can continue the patient's care. This request is for treatment purposes.</p>"
    + "<p class=\"sig\">Thank you,<br>" + esc(rp ? rp.name : pr.name) + (rp && rp.npi ? "<br>NPI " + esc(rp.npi) : "") + "</p>"
    + "</body></html>";
}

// ---------------------------------------------------------------------------
// Prior auth form pick lists (Oct 2026). Every list still accepts typed text.

// Common prior auth requests with their usual code. Picking one fills the
// CPT / HCPCS box; staff can change either.
// Built-in catalog for the Service requested and CPT / HCPCS pick lists: services that
// commonly need a prior authorization from NC Medicaid plans and commercial payers.
// A practice adds its own on top (prior_auth_practice_services); staff can always type
// any service or code. Codes are the usual CPT / HCPCS for the service; staff confirm
// the exact code for the request.
export const SERVICE_PRESETS = [
  { service: "MRI brain without contrast", code: "70551", category: "Imaging" },
  { service: "MRI brain with and without contrast", code: "70553", category: "Imaging" },
  { service: "MRI cervical spine without contrast", code: "72141", category: "Imaging" },
  { service: "MRI thoracic spine without contrast", code: "72146", category: "Imaging" },
  { service: "MRI lumbar spine without contrast", code: "72148", category: "Imaging" },
  { service: "MRI lumbar spine with and without contrast", code: "72158", category: "Imaging" },
  { service: "MRI shoulder without contrast", code: "73221", category: "Imaging" },
  { service: "MRI upper extremity joint (elbow, wrist) without contrast", code: "73221", category: "Imaging" },
  { service: "MRI knee without contrast", code: "73721", category: "Imaging" },
  { service: "MRI lower extremity joint (hip, ankle) without contrast", code: "73721", category: "Imaging" },
  { service: "MRI abdomen with and without contrast", code: "74183", category: "Imaging" },
  { service: "MRI pelvis with and without contrast", code: "72197", category: "Imaging" },
  { service: "MRI breast, bilateral, with and without contrast", code: "77049", category: "Imaging" },
  { service: "MRA head without contrast", code: "70544", category: "Imaging" },
  { service: "MRA neck without contrast", code: "70547", category: "Imaging" },
  { service: "CT head without contrast", code: "70450", category: "Imaging" },
  { service: "CT sinus (maxillofacial) without contrast", code: "70486", category: "Imaging" },
  { service: "CT cervical spine without contrast", code: "72125", category: "Imaging" },
  { service: "CT lumbar spine without contrast", code: "72131", category: "Imaging" },
  { service: "CT chest without contrast", code: "71250", category: "Imaging" },
  { service: "CT chest with contrast", code: "71260", category: "Imaging" },
  { service: "CT angiography chest", code: "71275", category: "Imaging" },
  { service: "Low-dose CT lung cancer screening", code: "71271", category: "Imaging" },
  { service: "CT abdomen and pelvis with contrast", code: "74177", category: "Imaging" },
  { service: "CT abdomen and pelvis without contrast", code: "74176", category: "Imaging" },
  { service: "PET/CT (skull base to mid-thigh)", code: "78815", category: "Imaging" },
  { service: "Nuclear stress test (myocardial perfusion)", code: "78452", category: "Imaging" },
  { service: "Echocardiogram (transthoracic)", code: "93306", category: "Cardiology" },
  { service: "Stress echocardiogram", code: "93351", category: "Cardiology" },
  { service: "Transesophageal echocardiogram", code: "93312", category: "Cardiology" },
  { service: "Holter monitor (up to 48 hours)", code: "93224", category: "Cardiology" },
  { service: "Extended heart monitor (2 to 7 days)", code: "93241", category: "Cardiology" },
  { service: "Mobile cardiac telemetry", code: "93228", category: "Cardiology" },
  { service: "Cardiac catheterization (coronary angiography)", code: "93454", category: "Cardiology" },
  { service: "Cardiac rehabilitation", code: "93798", category: "Cardiology" },
  { service: "Sleep study (in-lab polysomnography)", code: "95810", category: "Sleep" },
  { service: "Sleep study with CPAP titration (split night)", code: "95811", category: "Sleep" },
  { service: "Home sleep apnea test", code: "95806", category: "Sleep" },
  { service: "Upper GI endoscopy", code: "43235", category: "GI" },
  { service: "Colonoscopy (diagnostic)", code: "45378", category: "GI" },
  { service: "Colonoscopy with biopsy", code: "45380", category: "GI" },
  { service: "Capsule endoscopy", code: "91110", category: "GI" },
  { service: "Knee arthroscopy with meniscectomy", code: "29881", category: "Procedures and surgery" },
  { service: "Total knee replacement", code: "27447", category: "Procedures and surgery" },
  { service: "Total hip replacement", code: "27130", category: "Procedures and surgery" },
  { service: "Carpal tunnel release", code: "64721", category: "Procedures and surgery" },
  { service: "Laparoscopic gallbladder removal", code: "47562", category: "Procedures and surgery" },
  { service: "Tonsillectomy (age 12 and over)", code: "42826", category: "Procedures and surgery" },
  { service: "Septoplasty", code: "30520", category: "Procedures and surgery" },
  { service: "Sleeve gastrectomy (bariatric)", code: "43775", category: "Procedures and surgery" },
  { service: "Cataract surgery", code: "66984", category: "Procedures and surgery" },
  { service: "Lumbar epidural steroid injection", code: "62323", category: "Procedures and surgery" },
  { service: "Lumbar facet joint injection", code: "64493", category: "Procedures and surgery" },
  { service: "Lumbar facet radiofrequency ablation", code: "64635", category: "Procedures and surgery" },
  { service: "Spinal cord stimulator trial", code: "63650", category: "Procedures and surgery" },
  { service: "Lumbar laminotomy / discectomy", code: "63030", category: "Procedures and surgery" },
  { service: "Varicose vein ablation", code: "36475", category: "Procedures and surgery" },
  { service: "Breast reduction", code: "19318", category: "Procedures and surgery" },
  { service: "Upper eyelid blepharoplasty", code: "15823", category: "Procedures and surgery" },
  { service: "Botox injections for chronic migraine", code: "64615", category: "Procedures and surgery" },
  { service: "Removal of skin tags", code: "11200", category: "Procedures and surgery" },
  { service: "Contraceptive implant insertion", code: "11981", category: "Procedures and surgery" },
  { service: "IUD insertion", code: "58300", category: "Procedures and surgery" },
  { service: "Physical therapy evaluation", code: "97161", category: "Therapy" },
  { service: "Physical therapy evaluation (moderate complexity)", code: "97162", category: "Therapy" },
  { service: "Physical therapy evaluation (high complexity)", code: "97163", category: "Therapy" },
  { service: "Physical therapy (therapeutic exercise)", code: "97110", category: "Therapy" },
  { service: "Physical therapy (manual therapy)", code: "97140", category: "Therapy" },
  { service: "Physical therapy (neuromuscular re-education)", code: "97112", category: "Therapy" },
  { service: "Occupational therapy evaluation", code: "97165", category: "Therapy" },
  { service: "Speech therapy evaluation", code: "92523", category: "Therapy" },
  { service: "Speech therapy treatment", code: "92507", category: "Therapy" },
  { service: "Pulmonary rehabilitation", code: "94625", category: "Therapy" },
  { service: "Chiropractic manipulation", code: "98940", category: "Therapy" },
  { service: "Psychiatric diagnostic evaluation", code: "90791", category: "Behavioral health" },
  { service: "Psychotherapy, 45 minutes", code: "90834", category: "Behavioral health" },
  { service: "Psychotherapy, 60 minutes", code: "90837", category: "Behavioral health" },
  { service: "Psychological testing", code: "96130", category: "Behavioral health" },
  { service: "Neuropsychological testing", code: "96132", category: "Behavioral health" },
  { service: "ABA assessment", code: "97151", category: "Behavioral health" },
  { service: "ABA therapy (by technician)", code: "97153", category: "Behavioral health" },
  { service: "Substance use intensive outpatient", code: "H0015", category: "Behavioral health" },
  { service: "Partial hospitalization (mental health)", code: "H0035", category: "Behavioral health" },
  { service: "Child and adolescent day treatment", code: "H2012", category: "Behavioral health" },
  { service: "Multisystemic therapy", code: "H2033", category: "Behavioral health" },
  { service: "TMS therapy (initial)", code: "90867", category: "Behavioral health" },
  { service: "TMS therapy (subsequent)", code: "90868", category: "Behavioral health" },
  { service: "Home health skilled nursing visit", code: "G0299", category: "Home and community" },
  { service: "Home health aide visit", code: "G0156", category: "Home and community" },
  { service: "Home health physical therapy", code: "G0151", category: "Home and community" },
  { service: "Personal care services", code: "99509", category: "Home and community" },
  { service: "Private duty nursing", code: "T1000", category: "Home and community" },
  { service: "Specialist consult (new patient visit)", code: "99204", category: "Specialist and testing" },
  { service: "Specialist consult, complex (new patient visit)", code: "99205", category: "Specialist and testing" },
  { service: "Allergy skin testing", code: "95004", category: "Specialist and testing" },
  { service: "Allergy immunotherapy", code: "95165", category: "Specialist and testing" },
  { service: "Hearing evaluation (audiometry)", code: "92557", category: "Specialist and testing" },
  { service: "Hearing aids (binaural, behind the ear)", code: "V5261", category: "Specialist and testing" },
  { service: "BRCA1/BRCA2 genetic testing", code: "81162", category: "Specialist and testing" },
  { service: "Hereditary cancer genetic panel", code: "81432", category: "Specialist and testing" },
  { service: "Prenatal cell-free DNA screening (NIPT)", code: "81420", category: "Specialist and testing" },
  { service: "Denosumab (Prolia) injection", code: "J0897", category: "Injectables and infusions" },
  { service: "OnabotulinumtoxinA (Botox)", code: "J0585", category: "Injectables and infusions" },
  { service: "Infliximab infusion", code: "J1745", category: "Injectables and infusions" },
  { service: "Iron sucrose infusion", code: "J1756", category: "Injectables and infusions" },
  { service: "Ferric carboxymaltose (Injectafer) infusion", code: "J1439", category: "Injectables and infusions" },
  { service: "Omalizumab (Xolair) injection", code: "J2357", category: "Injectables and infusions" },
  { service: "Medroxyprogesterone (Depo-Provera) injection", code: "J1050", category: "Injectables and infusions" },
  { service: "Testosterone cypionate injection", code: "J1071", category: "Injectables and infusions" },
  { service: "Aripiprazole long-acting injection (Abilify Maintena)", code: "J0401", category: "Injectables and infusions" },
  { service: "Paliperidone palmitate injection (Invega Sustenna)", code: "J2426", category: "Injectables and infusions" },
  { service: "Naltrexone extended-release injection (Vivitrol)", code: "J2315", category: "Injectables and infusions" },
  { service: "Rituximab infusion", code: "J9312", category: "Injectables and infusions" },
  { service: "Pegfilgrastim injection", code: "J2506", category: "Injectables and infusions" },
  { service: "Knee gel injection (Synvisc)", code: "J7325", category: "Injectables and infusions" },
  { service: "Contraceptive implant (Nexplanon)", code: "J7307", category: "Injectables and infusions" },
  { service: "Hormonal IUD (Mirena)", code: "J7298", category: "Injectables and infusions" },
  { service: "CPAP device", code: "E0601", category: "DME and supplies" },
  { service: "BiPAP device (without backup rate)", code: "E0470", category: "DME and supplies" },
  { service: "BiPAP device (with backup rate)", code: "E0471", category: "DME and supplies" },
  { service: "CPAP full face mask", code: "A7030", category: "DME and supplies" },
  { service: "Home oxygen concentrator", code: "E1390", category: "DME and supplies" },
  { service: "Portable oxygen concentrator", code: "E1392", category: "DME and supplies" },
  { service: "Nebulizer", code: "E0570", category: "DME and supplies" },
  { service: "Standard manual wheelchair", code: "K0001", category: "DME and supplies" },
  { service: "Lightweight manual wheelchair", code: "K0003", category: "DME and supplies" },
  { service: "Power wheelchair (group 2)", code: "K0823", category: "DME and supplies" },
  { service: "Power scooter", code: "K0800", category: "DME and supplies" },
  { service: "Hospital bed (semi-electric)", code: "E0260", category: "DME and supplies" },
  { service: "Pressure-reducing air mattress", code: "E0277", category: "DME and supplies" },
  { service: "Patient lift (hydraulic)", code: "E0630", category: "DME and supplies" },
  { service: "Folding walker with wheels", code: "E0143", category: "DME and supplies" },
  { service: "Bedside commode", code: "E0163", category: "DME and supplies" },
  { service: "Shower chair", code: "E0240", category: "DME and supplies" },
  { service: "Seat lift chair mechanism", code: "E0627", category: "DME and supplies" },
  { service: "TENS unit", code: "E0730", category: "DME and supplies" },
  { service: "Continuous glucose monitor (receiver)", code: "E2103", category: "DME and supplies" },
  { service: "Continuous glucose monitor supplies", code: "A4239", category: "DME and supplies" },
  { service: "Insulin pump", code: "E0784", category: "DME and supplies" },
  { service: "Insulin pump infusion sets", code: "A4230", category: "DME and supplies" },
  { service: "Blood glucose monitor", code: "E0607", category: "DME and supplies" },
  { service: "Blood glucose test strips", code: "A4253", category: "DME and supplies" },
  { service: "Breast pump (electric)", code: "E0603", category: "DME and supplies" },
  { service: "Compression stockings (30-40 mmHg)", code: "A6531", category: "DME and supplies" },
  { service: "Diabetic shoes", code: "A5500", category: "DME and supplies" },
  { service: "Wound vac (negative pressure pump)", code: "E2402", category: "DME and supplies" },
  { service: "Enteral feeding pump", code: "B9002", category: "DME and supplies" },
  { service: "Enteral formula", code: "B4150", category: "DME and supplies" },
  { service: "Apnea monitor", code: "E0618", category: "DME and supplies" },
];
// The practice's own entries win over the catalog for the same service name.
export function presetFor(service, practiceItems) {
  const s = String(service || "").trim().toLowerCase();
  if (!s) return null;
  const own = (practiceItems || []).find((p) => String(p.service || "").trim().toLowerCase() === s);
  if (own) return { service: own.service, code: own.code || "", category: "Your list" };
  return SERVICE_PRESETS.find((p) => p.service.toLowerCase() === s) || null;
}

// Service pick list: the practice's list first, then the catalog.
export function serviceOptions(practiceItems) {
  const seen = new Set();
  const out = [];
  for (const p of practiceItems || []) {
    const k = String(p.service || "").trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push({ value: p.service, label: (p.code ? p.code + " - " : "") + "Your list", id: p.id, own: true });
  }
  for (const p of SERVICE_PRESETS) {
    const k = p.service.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ value: p.service, label: p.code + " - " + p.category });
  }
  return out;
}

// CPT / HCPCS pick list: practice codes, then catalog codes, then the code master.
export function codeOptions(practiceItems, codeMaster) {
  const seen = new Set();
  const out = [];
  const add = (code, label) => {
    const c = String(code || "").trim().toUpperCase();
    if (!c || seen.has(c)) return;
    seen.add(c);
    out.push({ value: c, label: label || "" });
  };
  for (const p of practiceItems || []) add(p.code, p.service + " (your list)");
  for (const p of SERVICE_PRESETS) add(p.code, p.service);
  for (const c of codeMaster || []) add(c.code, c.description);
  return out;
}

// Offer "Save to our list" when the typed service (and code) is new to the practice.
export function canSaveService(service, code, practiceItems) {
  const s = String(service || "").trim().replace(/\s+/g, " ");
  if (s.length < 2 || s.length > 120) return false;
  const c = String(code || "").trim().toUpperCase();
  if (c && !/^[A-Z0-9]{4,7}$/.test(c)) return false;
  const k = s.toLowerCase();
  const own = (practiceItems || []).find((p) => String(p.service || "").trim().toLowerCase() === k);
  if (own) return !!c && c !== String(own.code || "").toUpperCase();
  const preset = SERVICE_PRESETS.find((p) => p.service.toLowerCase() === k);
  if (preset) return !!c && c !== preset.code;
  return true;
}

export const UNIT_TYPES = ["visits", "units", "days", "sessions", "items", "months"];
// Units are stored as one text value, e.g. "12 visits". Older free-text
// values that do not fit come back as the number box text with no type.
export function splitUnits(v) {
  const s = String(v || "").trim();
  const m = s.match(/^(\d+)\s*([A-Za-z]+)?$/);
  if (!m) return { n: s, type: "" };
  const t = (m[2] || "").toLowerCase();
  return UNIT_TYPES.indexOf(t) !== -1 ? { n: m[1], type: t } : { n: s, type: "" };
}
export function joinUnits(n, type) {
  const num = String(n || "").trim();
  if (!num) return "";
  return type && /^\d+$/.test(num) ? num + " " + type : num;
}

// Diagnosis codes are stored as one comma-separated text value.
export function parseCodes(v) {
  const seen = new Set();
  return String(v || "").split(/[,;\s]+/).map((c) => c.trim().toUpperCase()).filter((c) => {
    if (!c || seen.has(c)) return false;
    seen.add(c); return true;
  });
}
export function joinCodes(list) {
  return parseCodes((list || []).join(",")).join(", ");
}
export function toggleCode(v, code) {
  const list = parseCodes(v);
  const c = String(code || "").trim().toUpperCase();
  if (!c) return joinCodes(list);
  return joinCodes(list.indexOf(c) !== -1 ? list.filter((x) => x !== c) : list.concat([c]));
}
export function looksLikeIcd10(c) {
  return /^[A-TV-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/.test(String(c || "").trim().toUpperCase());
}

// Payer choices: the patient's active coverage first (marked), then the NC
// payer list without duplicates.
export function payerOptions(opts) {
  const out = [];
  const seen = new Set();
  const add = (value, hint) => {
    const k = String(value || "").trim().toLowerCase();
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push({ value: String(value).trim(), hint });
  };
  const cov = (opts && Array.isArray(opts.coverage)) ? opts.coverage : [];
  cov.forEach((c, i) => add(c.payer, (i === 0 ? "Patient's primary coverage" : "Patient's coverage") + (c.plan ? " - " + c.plan : "")));
  ((opts && Array.isArray(opts.payers)) ? opts.payers : []).forEach((p) => add(p.name, String(p.type || "").replace(/_/g, " ")));
  return out;
}

// Suggestions for a pick-or-type field: options whose value or label contains
// every typed word (case-insensitive), values starting with the text first.
// An exact value match hides the list (the pick is made). Empty text shows the
// first `limit` options.
export function filterOptions(options, text, limit) {
  const max = limit || 8;
  const list = Array.isArray(options) ? options : [];
  const q = String(text || "").trim().toLowerCase();
  if (!q) return list.slice(0, max);
  if (list.some((o) => String(o.value).toLowerCase() === q)) return [];
  const words = q.split(/\s+/);
  const hits = list.filter((o) => {
    const hay = (String(o.value) + " " + String(o.label || "")).toLowerCase();
    return words.every((w) => hay.indexOf(w) !== -1);
  });
  const starts = hits.filter((o) => String(o.value).toLowerCase().indexOf(q) === 0);
  const rest = hits.filter((o) => String(o.value).toLowerCase().indexOf(q) !== 0);
  return starts.concat(rest).slice(0, max);
}

// Links into the Prior auths tab (Inbox, tasks): one auth open, or a new
// request started for a patient.
export const authLink = (authId) => "/referrals?tab=auths&auth=" + encodeURIComponent(authId);
export const newAuthLink = (patientId) => "/referrals?tab=auths&new=1" + (patientId ? "&patient=" + encodeURIComponent(patientId) : "");
// A task about a prior auth: linked to one, or filed under Prior Auth.
export const isPriorAuthTask = (task) => !!task && String(task.category || "") === "Prior Auth";

// ---------------------------------------------------------------------------
// Auth Clock (Nov 2026). CMS-0057-F: from Jan 1 2026 Medicaid managed care,
// Medicaid fee-for-service and Medicare Advantage must decide a prior auth
// within 72 hours (expedited) or 7 calendar days (standard), and give a
// specific reason for a denial. Commercial plans are not covered. The server
// works out the rule from the payer (prior_authorizations.clock_rule) and the
// due time (plan_due_at); migration 20270224100000_prior_auth_clock.
// ---------------------------------------------------------------------------
export const CLOCK_RULES = {
  medicaid_managed_care: { label: "NC Medicaid health plan", cite: "42 CFR 438.210(d)" },
  medicaid_ffs: { label: "NC Medicaid Direct", cite: "42 CFR 440.230(e)" },
  medicare_advantage: { label: "Medicare Advantage", cite: "42 CFR 422.568 and 422.572" },
  none: { label: "No federal clock", cite: null },
};
export const CLOCK_RULE_OPTIONS = [
  { value: "auto", label: "From the payer" },
  { value: "medicaid_managed_care", label: "NC Medicaid health plan (Standard or Tailored)" },
  { value: "medicaid_ffs", label: "NC Medicaid Direct" },
  { value: "medicare_advantage", label: "Medicare Advantage" },
  { value: "none", label: "No federal clock (commercial, other)" },
];
export const URGENCY_OPTIONS = [
  { value: "standard", label: "Standard - 7 days" },
  { value: "expedited", label: "Expedited - 72 hours" },
];

function hm(ms) {
  const h = Math.floor(Math.abs(ms) / 3600000);
  if (h >= 48) return Math.floor(h / 24) + " days";
  if (h >= 24) return "1 day " + (h - 24) + " h";
  const m = Math.floor((Math.abs(ms) % 3600000) / 60000);
  return h > 0 ? h + " h " + m + " min" : m + " min";
}

export function dueLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// The decision clock for one request. now: a Date (tests pass one).
// Returns null when no federal clock applies or nothing was submitted.
export function clockView(a, now) {
  if (!a || !a.plan_due_at || !a.submitted_at) return null;
  const rule = CLOCK_RULES[a.clock_rule] || null;
  const t = (now instanceof Date ? now : new Date()).getTime();
  const start = new Date(a.submitted_at).getTime();
  const due = new Date(a.plan_due_at).getTime();
  if (isNaN(start) || isNaN(due) || due <= start) return null;
  const pending = a.status === "Pending";
  const left = due - t;
  const pct = Math.max(0, Math.min(100, Math.round(((t - start) / (due - start)) * 100)));
  const windowText = a.urgency === "expedited" ? "72 hours" : "7 days";
  const limitText = a.urgency === "expedited" ? "72-hour" : "7-day";
  const out = {
    rule: a.clock_rule, ruleLabel: rule ? rule.label : "", cite: rule ? rule.cite : null,
    windowText, dueText: dueLabel(a.plan_due_at), pct: pending ? pct : 100,
    late: pending && left < 0, soon: pending && left >= 0 && left <= 24 * 3600000,
    tone: "blue", headline: "", sub: "",
  };
  if (!pending) {
    if (a.decided_on_time === true) { out.tone = "green"; out.headline = "Plan decided within " + windowText; }
    else if (a.decided_on_time === false) { out.tone = "red"; out.headline = "Plan decided after the " + limitText + " limit"; }
    else out.headline = "Plan decision due " + out.dueText;
    out.sub = out.ruleLabel + (out.cite ? " - " + out.cite : "");
    return out;
  }
  if (out.late) {
    out.tone = "red";
    out.headline = "Plan is " + hm(left) + " late";
    out.sub = "The " + limitText + " limit passed " + out.dueText + ". Send the overdue letter and call the plan.";
  } else if (out.soon) {
    out.tone = "amber";
    out.headline = "Plan decision due in " + hm(left);
    out.sub = "Due " + out.dueText + " (" + windowText + ", " + out.ruleLabel + ").";
  } else {
    out.headline = "Plan decision due in " + hm(left);
    out.sub = "Due " + out.dueText + " (" + windowText + ", " + out.ruleLabel + ").";
  }
  return out;
}

const RULE_TEXT = {
  medicaid_managed_care: "Under 42 CFR 438.210(d), as amended by the CMS Interoperability and Prior Authorization Final Rule (CMS-0057-F), a Medicaid managed care plan must decide a standard prior authorization request within 7 calendar days and an expedited request within 72 hours.",
  medicaid_ffs: "Under 42 CFR 440.230(e), as amended by the CMS Interoperability and Prior Authorization Final Rule (CMS-0057-F), a state Medicaid fee-for-service program must decide a standard prior authorization request within 7 calendar days and an expedited request within 72 hours.",
  medicare_advantage: "Under 42 CFR 422.568 and 422.572, as amended by the CMS Interoperability and Prior Authorization Final Rule (CMS-0057-F), a Medicare Advantage organization must decide a standard prior authorization request within 7 calendar days and an expedited request within 72 hours.",
};

function fmtLocalStamp(s) {
  // "YYYY-MM-DDTHH:MI" in the practice's zone (from the server).
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(s));
  if (!m) return String(s);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return d.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// Printable overdue letter to the plan (prior_auth_overdue_letter data).
export function overdueLetterHtml(d, todayLabel) {
  const pr = (d && d.practice) || {};
  const p = (d && d.patient) || {};
  const r = (d && d.request) || {};
  const pv = d && d.provider;
  const exp = r.urgency === "expedited";
  const via = { portal: "the plan's provider portal", fax: "fax", phone: "phone", mail: "mail" }[r.submitted_via] || "the plan's usual channel";
  return "<!doctype html><html><head><meta charset=\"utf-8\"><title>Prior authorization decision overdue</title>"
    + "<style>body{font-family:Georgia,serif;max-width:720px;margin:40px auto;color:#111;line-height:1.5;font-size:14px}"
    + "h1{font-size:18px;margin:0}table{border-collapse:collapse;margin:12px 0}td{padding:4px 12px 4px 0;vertical-align:top}"
    + ".muted{color:#555}.sig{margin-top:40px}.callout{border-left:3px solid #111;padding:6px 12px;margin:14px 0}@media print{button{display:none}}</style></head><body>"
    // audit-exempt: the server writes the Export audit row (prior_auth_overdue_letter) before this letter is built.
    + "<button onclick=\"window.print()\" style=\"float:right\">Print</button>"
    + "<h1>" + esc(pr.name) + "</h1><div class=\"muted\">" + esc(pr.address || "") + "<br>Phone " + esc(pr.phone || "")
    + (pr.fax ? " - Fax " + esc(pr.fax) : "") + (pr.npi ? " - NPI " + esc(pr.npi) : "") + "</div>"
    + "<p>" + esc(todayLabel || "") + "</p>"
    + "<p>To: " + esc(r.payer || "Health plan") + " - Utilization Management / Prior Authorization</p>"
    + "<p><b>" + (exp ? "EXPEDITED - " : "") + "Prior authorization decision overdue" + (d && d.letter_number > 1 ? " (notice " + esc(d.letter_number) + ")" : "") + "</b></p>"
    + "<table><tr><td>Member</td><td>" + esc(p.name) + "</td></tr><tr><td>Date of birth</td><td>" + esc(p.dob || "") + "</td></tr>"
    + (p.member_id ? "<tr><td>Member ID</td><td>" + esc(p.member_id) + "</td></tr>" : "")
    + "<tr><td>Service requested</td><td>" + esc(r.service || "") + (r.service_code ? " (" + esc(r.service_code) + ")" : "") + (r.units ? ", " + esc(r.units) : "") + "</td></tr>"
    + (r.diagnosis_codes ? "<tr><td>Diagnoses</td><td>" + esc(r.diagnosis_codes) + "</td></tr>" : "")
    + (r.payer_ref ? "<tr><td>Your reference</td><td>" + esc(r.payer_ref) + "</td></tr>" : "")
    + "<tr><td>Submitted</td><td>" + esc(fmtLocalStamp(r.submitted_at)) + " by " + esc(via) + "</td></tr>"
    + "<tr><td>Decision was due</td><td>" + esc(fmtLocalStamp(r.plan_due_at)) + "</td></tr></table>"
    + "<p>We submitted the " + (exp ? "expedited " : "") + "prior authorization request above and have not received a decision. "
    + (RULE_TEXT[r.clock_rule] ? esc(RULE_TEXT[r.clock_rule]) + " " : "")
    + "That time has passed.</p>"
    + "<div class=\"callout\">Please issue a decision on this request today and send it to our office"
    + (pr.fax ? " by fax at " + esc(pr.fax) : "") + ". If the request is denied, please state the specific reason for the denial, as the rule requires.</div>"
    + "<p>If you need anything else to decide, call us at " + esc(pr.phone || "our office") + ".</p>"
    + "<p class=\"sig\">Thank you,<br>" + esc(pv ? pv.name + (pv.credential ? ", " + pv.credential : "") : pr.name)
    + (pv && pv.npi ? "<br>NPI " + esc(pv.npi) : "") + "<br>" + esc(pr.name) + "</p>"
    + "</body></html>";
}

// One line for a decided request's clock, e.g. on the work queue row.
export function clockRowText(a, now) {
  const v = clockView(a, now);
  if (!v) return "";
  if (a.status === "Pending") return v.late ? v.headline : "plan due " + v.dueText;
  return v.headline;
}

// ---------------------------------------------------------------------------
// Prior auth timeline, row countdown and plan scorecard (Nov 2026).
// ---------------------------------------------------------------------------

// Short countdown for a work queue row: "2d 4h", "5h 12m", "40m", or
// "3h late". tone: red when late, amber inside 24 hours, else null.
// Returns null when the request has no live clock.
export function countdownShort(a, now) {
  if (!a || a.status !== "Pending" || !a.plan_due_at) return null;
  const due = new Date(a.plan_due_at).getTime();
  if (isNaN(due)) return null;
  const t = (now instanceof Date ? now : new Date()).getTime();
  const left = due - t;
  const ms = Math.abs(left);
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const text = d > 0 ? d + "d " + h + "h" : h > 0 ? h + "h " + m + "m" : m + "m";
  if (left < 0) return { text: text + " late", tone: "red", late: true, label: "Plan decision " + text + " late" };
  return { text, tone: left <= 24 * 3600000 ? "amber" : null, late: false, label: "Plan decision due in " + text };
}

// The request's path as steps: Started, Submitted, Plan due, Decided, and
// Appeal when denied. state: done | current | late | upcoming | missed.
export function authTimeline(a, now) {
  if (!a) return [];
  const t = (now instanceof Date ? now : new Date()).getTime();
  const decided = ["Approved", "Partially approved", "Denied"].includes(a.status);
  const cancelled = a.status === "Cancelled";
  const submitted = !!a.submitted_at;
  const due = a.plan_due_at ? new Date(a.plan_due_at).getTime() : null;
  const steps = [];
  steps.push({ key: "started", label: "Started", date: a.created_at || null, state: "done" });
  steps.push({
    key: "submitted", label: "Submitted", date: a.submitted_at || null,
    state: submitted ? "done" : cancelled ? "missed" : a.status === "Draft" ? "current" : "upcoming",
    note: submitted && a.urgency === "expedited" ? "Expedited" : null,
  });
  if (due != null && !isNaN(due)) {
    let st = "upcoming";
    if (decided) st = a.decided_on_time === false ? "late" : "done";
    else if (a.status === "Pending") st = t > due ? "late" : "current";
    else if (cancelled) st = "missed";
    steps.push({ key: "due", label: decided ? (a.decided_on_time === false ? "Plan was late" : "Plan deadline") : "Plan due", date: a.plan_due_at, state: st });
  }
  steps.push({
    key: "decided", label: decided ? a.status : cancelled ? "Cancelled" : "Decision", date: decided ? a.decision_date || null : null,
    state: decided ? (a.status === "Denied" ? "late" : "done") : cancelled ? "missed" : due == null && a.status === "Pending" ? "current" : "upcoming",
  });
  if (a.status === "Denied" || (a.appeal_status && a.appeal_status !== "none")) {
    const ap = a.appeal_status || "none";
    const map = { won: "done", lost: "late", submitted: "current", planned: "current", none: "current" };
    const label = { won: "Appeal won", lost: "Appeal lost", submitted: "Appeal sent", planned: "Appeal planned", none: "Appeal open" }[ap] || "Appeal open";
    steps.push({ key: "appeal", label, date: ap === "none" || ap === "planned" ? a.appeal_deadline || null : null, state: map[ap] || "current",
      note: (ap === "none" || ap === "planned") && a.appeal_deadline ? "by" : null });
  }
  return steps;
}

// Plan scorecard rows from prior_auth_plan_scorecard, worst on-time rate
// first. pct is null when nothing has been decided under a clock yet.
export function scorecardRows(d) {
  const rows = (d && Array.isArray(d.payers) ? d.payers : []).map((p) => {
    const decided = Number(p.decided) || 0;
    const onTime = Number(p.on_time) || 0;
    return {
      payer: p.payer || "Unknown payer", rule: p.rule || null, decided, onTime, late: Number(p.late) || 0,
      openOverdue: Number(p.open_overdue) || 0, open: Number(p.open) || 0,
      medianDays: p.median_days == null ? null : Number(p.median_days),
      pct: decided ? Math.round((onTime / decided) * 100) : null,
    };
  });
  rows.sort((a, b) => {
    if (b.openOverdue !== a.openOverdue) return b.openOverdue - a.openOverdue;
    const ap = a.pct == null ? 101 : a.pct;
    const bp = b.pct == null ? 101 : b.pct;
    if (ap !== bp) return ap - bp;
    return a.payer.localeCompare(b.payer);
  });
  return rows;
}
export function scoreTone(pct) {
  if (pct == null) return "neutral";
  if (pct >= 90) return "green";
  if (pct >= 70) return "amber";
  return "red";
}

// ---------------------------------------------------------------------------
// "Does this need prior auth?" (Nov 2026). Rules the practice keeps until each
// plan's Prior Authorization API (CMS-0057-F, Jan 1 2027) can answer it;
// migration 20270228100000_prior_auth_rules.
// ---------------------------------------------------------------------------
export const REQUIREMENT_OPTIONS = [
  { value: "required", label: "Prior auth required" },
  { value: "not_required", label: "No prior auth needed" },
  { value: "varies", label: "Depends - check the plan" },
];
export const PLAN_TYPE_OPTIONS = [
  { value: "standard_plan", label: "NC Medicaid Standard Plans" },
  { value: "tailored_plan", label: "NC Medicaid Tailored Plans" },
  { value: "medicaid_direct", label: "NC Medicaid Direct" },
  { value: "medicare_advantage", label: "Medicare Advantage" },
  { value: "medicare", label: "Medicare (traditional)" },
  { value: "commercial", label: "Commercial plans" },
  { value: "other", label: "Other plans" },
];
export const SCOPE_OPTIONS = [
  { value: "payer", label: "One payer" },
  { value: "plan_type", label: "A type of plan" },
  { value: "any", label: "Any payer" },
];
export const normCode = (v) => String(v || "").toUpperCase().replace(/\s+/g, "");
export const isCheckableCode = (v) => /^[A-Z0-9]{4,7}$/.test(normCode(v));
const planTypeLabel = (v) => (PLAN_TYPE_OPTIONS.find((o) => o.value === v) || {}).label || "";

// Who a rule applies to, in words.
export function ruleScopeText(r) {
  if (!r) return "";
  if (r.payer) return r.payer;
  if (r.plan_type) return planTypeLabel(r.plan_type);
  return "Any payer";
}
export function ruleCodeText(code) {
  const c = String(code || "");
  return /\*$/.test(c) ? "codes starting " + c.slice(0, -1) : c;
}

// The check result as one line: { tone, title, sub, status }.
export function requirementView(res) {
  if (!res) return null;
  const h = res.history || {};
  const n = Number(h.requests) || 0;
  const hist = n ? "You sent " + n + (n === 1 ? " request" : " requests") + " to this payer for " + res.code + " in the last year"
    + (Number(h.approved) || Number(h.denied) ? " (" + [Number(h.approved) ? h.approved + " approved" : null, Number(h.denied) ? h.denied + " denied" : null].filter(Boolean).join(", ") + ")" : "") + "." : "";
  const who = res.payer ? res.payer : "this patient's plan";
  const by = res.rule ? "Practice rule for " + ruleScopeText(res.rule) + ", " + ruleCodeText(res.rule.code) + (res.rule.note ? ": " + res.rule.note : "") + "." : "";
  if (res.status === "required") return { status: "required", tone: "amber", title: who + " requires prior auth for " + res.code, sub: [by, hist].filter(Boolean).join(" ") };
  if (res.status === "not_required") return { status: "not_required", tone: "green", title: "No prior auth needed for " + res.code + " with " + who, sub: [by, hist].filter(Boolean).join(" ") };
  if (res.status === "varies") return { status: "varies", tone: "blue", title: "Prior auth for " + res.code + " depends - check with " + who, sub: [by, hist].filter(Boolean).join(" ") };
  return {
    status: "unknown", tone: "neutral",
    title: res.payer ? "No rule yet for " + res.code + " with " + res.payer : "Add the patient's coverage to check " + res.code,
    sub: hist || (res.payer ? "Check the plan's prior auth list, then add a rule so the next person knows." : ""),
  };
}

export function ruleProblems(f) {
  const p = [];
  const code = normCode(f && f.code);
  if (!/^[A-Z0-9]{1,7}\*?$/.test(code) || (!/\*$/.test(code) && code.length < 4)) p.push("Enter a CPT / HCPCS code (like 72148), or the start of one with * (like 7214*).");
  if (f && f.scope === "payer" && String(f.payer || "").trim().length < 2) p.push("Pick the payer.");
  if (f && f.scope === "plan_type" && !f.plan_type) p.push("Pick the type of plan.");
  if (!f || !REQUIREMENT_OPTIONS.some((o) => o.value === f.requirement)) p.push("Pick whether prior auth is required.");
  if (f && f.note && String(f.note).length > 500) p.push("Keep the note under 500 characters.");
  return p;
}

// ---------------------------------------------------------------------------
// Denial patterns and appeal results (Nov 2026), from prior_auth_denial_insights.
// ---------------------------------------------------------------------------
const pct = (a, b) => (Number(b) > 0 ? Math.round((Number(a) / Number(b)) * 100) : null);
export function denialSummary(d) {
  const t = (d && d.totals) || {};
  const decided = Number(t.decided) || 0;
  const denied = Number(t.denied) || 0;
  const won = Number(t.won) || 0;
  const lost = Number(t.lost) || 0;
  return {
    decided, denied, denialRate: pct(denied, decided),
    appealed: Number(t.appealed) || 0, won, lost, open: Number(t.open) || 0,
    notAppealed: Number(t.not_appealed) || 0,
    winRate: pct(won, won + lost),
  };
}
export function denialPayerRows(d) {
  return (d && Array.isArray(d.payers) ? d.payers : [])
    .map((p) => ({ payer: p.payer, decided: Number(p.decided) || 0, denied: Number(p.denied) || 0, appealed: Number(p.appealed) || 0,
      won: Number(p.won) || 0, lost: Number(p.lost) || 0 }))
    .map((p) => ({ ...p, denialRate: pct(p.denied, p.decided), winRate: pct(p.won, p.won + p.lost) }))
    .filter((p) => p.decided > 0)
    .sort((a, b) => (b.denialRate ?? -1) - (a.denialRate ?? -1) || b.denied - a.denied || a.payer.localeCompare(b.payer));
}
export function denialServiceRows(d) {
  return (d && Array.isArray(d.services) ? d.services : []).map((s) => ({
    code: s.code || null, service: s.service || "Service", decided: Number(s.decided) || 0, denied: Number(s.denied) || 0,
    appealed: Number(s.appealed) || 0, won: Number(s.won) || 0, denialRate: pct(s.denied, s.decided),
  }));
}
export function denialReasonRows(d) {
  return (d && Array.isArray(d.reasons) ? d.reasons : []).map((r) => ({
    reason: String(r.reason || ""), count: Number(r.count) || 0, payers: Array.isArray(r.payers) ? r.payers : [],
  }));
}
